import React, { useState, useRef, useCallback, useEffect } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Box
} from '@material-ui/core'
import { enhancedGetPolygonArea, getGeometryFromLayer } from '../../MapWithDrawing/Measure/mapUtils'
import {
  applyBasemapProvider,
  getLatestMaricopaProvider,
  googleHybridProvider,
} from './maricopaBasemap'

const DEFAULT_CENTER = [33.505, -112.15]

const normalizePoint = (point = {}) => {
  const lat = Number(point.lat ?? point.latitude)
  const lng = Number(point.lng ?? point.lon ?? point.longitude)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const normalizePaths = (paths = []) => paths
  .map(normalizePoint)
  .filter(Boolean)

const roundAcres = (value) => Math.round((Number(value) || 0) * 100) / 100

const extractPolygonData = (layer) => {
  const geometry = getGeometryFromLayer(layer)
  const latlngs = Array.isArray(geometry?.latlngs) ? geometry.latlngs : []
  const paths = latlngs.map((ll) => ({ lat: ll.lat, lng: ll.lng }))
  const measurements = enhancedGetPolygonArea(paths)
  return {
    paths,
    areaSqft: Math.round(Number(measurements.areaFeet || 0)),
    areaAcres: roundAcres(measurements.areaAcres),
  }
}

const DrawZoneModal = ({ open, onClose, onAddZone, existingAreas, center }) => {
  const [drawnPaths, setDrawnPaths] = useState(null)
  const [label, setLabel] = useState('')
  const [acres, setAcres] = useState(0)
  const [mapContainerNode, setMapContainerNode] = useState(null)
  const mapRef = useRef(null)
  const tileLayerRef = useRef(null)
  const existingAreasLayerRef = useRef(null)
  const drawnItemsRef = useRef(null)
  const activePolygonRef = useRef(null)
  const drawHandlerRef = useRef(null)

  const syncFromLayer = useCallback((layer = activePolygonRef.current) => {
    if (!layer) return null
    const polygonData = extractPolygonData(layer)
    setDrawnPaths(polygonData.paths)
    setAcres(polygonData.areaAcres)
    return polygonData
  }, [])

  const stopDrawing = useCallback(() => {
    if (drawHandlerRef.current?.disable) {
      try { drawHandlerRef.current.disable() } catch (_) {}
    }
    drawHandlerRef.current = null
  }, [])

  const startPolygonDraw = useCallback(() => {
    const map = mapRef.current
    if (!map || activePolygonRef.current) return

    stopDrawing()

    const handler = new L.Draw.Polygon(map, {
      shapeOptions: {
        color: '#1565c0',
        weight: 2,
        fillColor: '#1976d2',
        fillOpacity: 0.18,
        opacity: 0.95,
      },
      allowIntersection: false,
      showArea: true,
    })

    const handleCreated = (e) => {
      map.off(L.Draw.Event.CREATED, handleCreated)
      drawHandlerRef.current = null

      const layer = e.layer
      drawnItemsRef.current?.addLayer(layer)
      activePolygonRef.current = layer

      try { layer.editing.enable() } catch (_) {}
      layer.on('edit', () => syncFromLayer(layer))
      layer.on('mouseup', () => syncFromLayer(layer))
      syncFromLayer(layer)
    }

    map.on(L.Draw.Event.CREATED, handleCreated)
    handler.enable()
    drawHandlerRef.current = handler
  }, [stopDrawing, syncFromLayer])

  const handleClear = useCallback((options = {}) => {
    const { restartDraw = true } = options
    stopDrawing()
    if (activePolygonRef.current && drawnItemsRef.current) {
      try { drawnItemsRef.current.removeLayer(activePolygonRef.current) } catch (_) {}
    }
    activePolygonRef.current = null
    setDrawnPaths(null)
    setAcres(0)
    setLabel('')
    if (restartDraw) {
      setTimeout(() => startPolygonDraw(), 0)
    }
  }, [startPolygonDraw, stopDrawing])

  const handleAddZone = useCallback(() => {
    const polygonData = syncFromLayer()
    if (!polygonData?.paths?.length || polygonData.paths.length < 3) return

    onAddZone({
      paths: polygonData.paths,
      area: polygonData.areaSqft,
      areaAcres: polygonData.areaAcres,
      label: label || 'Custom Zone',
      createdAt: new Date()
    })

    handleClear({ restartDraw: false })
    onClose()
  }, [handleClear, label, onAddZone, onClose, syncFromLayer])

  const handleClose = useCallback(() => {
    handleClear({ restartDraw: false })
    onClose()
  }, [handleClear, onClose])

  const handleMapContainerRef = useCallback((node) => {
    setMapContainerNode(node)
  }, [])

  useEffect(() => {
    if (!open || !mapContainerNode) return

    const map = L.map(mapContainerNode, {
      center: center ? [center.lat, center.lng] : DEFAULT_CENTER,
      zoom: center ? 18 : 11,
      maxZoom: 28,
      zoomControl: true,
    })
    mapRef.current = map

    existingAreasLayerRef.current = L.featureGroup().addTo(map)
    drawnItemsRef.current = new L.FeatureGroup()
    map.addLayer(drawnItemsRef.current)

    applyBasemapProvider({
      map,
      tileLayerRef,
      provider: googleHybridProvider,
    })

    let cancelled = false
    getLatestMaricopaProvider()
      .then((provider) => {
        if (cancelled || !mapRef.current) return
        applyBasemapProvider({
          map,
          tileLayerRef,
          provider,
        })
      })
      .catch((err) => {
        console.warn('Failed to load latest Maricopa basemap for custom spray zone:', err)
      })

    setTimeout(() => {
      try { map.invalidateSize() } catch (_) {}
      setTimeout(() => startPolygonDraw(), 0)
    }, 150)

    return () => {
      cancelled = true
      stopDrawing()
      if (tileLayerRef.current) {
        try { map.removeLayer(tileLayerRef.current) } catch (_) {}
        tileLayerRef.current = null
      }
      try { map.remove() } catch (_) {}
      mapRef.current = null
      existingAreasLayerRef.current = null
      drawnItemsRef.current = null
      activePolygonRef.current = null
    }
  }, [center, mapContainerNode, open, startPolygonDraw, stopDrawing])

  useEffect(() => {
    const map = mapRef.current
    const areaLayerGroup = existingAreasLayerRef.current
    if (!map || !areaLayerGroup) return

    areaLayerGroup.clearLayers()
    const bounds = L.latLngBounds([])

    ;(existingAreas || []).forEach((area) => {
      const paths = normalizePaths(area.paths)
      if (paths.length < 3) return

      const latlngs = paths.map((point) => [point.lat, point.lng])
      bounds.extend(latlngs)

      L.polygon(latlngs, {
        fillColor: area.fillColor || '#4caf50',
        fillOpacity: 0.08,
        color: area.strokeColor || '#4caf50',
        weight: 1,
        opacity: 0.45,
        interactive: false,
      }).addTo(areaLayerGroup)
    })

    if (activePolygonRef.current) {
      const polygonData = extractPolygonData(activePolygonRef.current)
      if (polygonData.paths.length >= 3) {
        bounds.extend(polygonData.paths.map((point) => [point.lat, point.lng]))
      }
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24] })
    } else if (center) {
      map.setView([center.lat, center.lng], 18)
    } else {
      map.setView(DEFAULT_CENTER, 11)
    }
  }, [center, existingAreas])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ style: { height: '85vh' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h6" style={{ fontWeight: 700 }}>Draw Custom Spray Zone</Typography>
            <Typography variant="caption" color="textSecondary">Leaflet + latest Maricopa aerials. Click to place points, double-click to finish.</Typography>
          </div>
          {drawnPaths && (
            <Typography variant="body2" style={{ color: '#1565c0', fontWeight: 600 }}>
              Drawn: {acres} acres
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: 0, position: 'relative', flex: 1 }}>
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: 8, padding: '6px 16px', background: '#f5f7f5', borderBottom: '1px solid #e8e8e8' }}
        >
          {drawnPaths ? (
            <>
              <Button size="small" variant="outlined" color="secondary" onClick={handleClear}>Clear &amp; Redraw</Button>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Zone label (optional)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                style={{ maxWidth: 220 }}
              />
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Drawing mode is active. Existing takeoff areas are shown faintly for reference.
            </Typography>
          )}
        </Box>

        <div style={{ position: 'absolute', top: 45, left: 0, right: 0, bottom: 0 }}>
          <div ref={handleMapContainerRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleAddZone}
          color="primary"
          variant="contained"
          disabled={!drawnPaths || drawnPaths.length < 3}
        >
          Add Zone{acres ? ` (${acres} ac)` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DrawZoneModal
